/**
 * The building blocks the shop guide and the training manuals share, so the
 * two read as one publication.
 */

import type { ReactNode } from "react";

import { Pill, TBody, TD, TH, THead, TR, Table, type PillTone } from "@/components/ui";
import { cn } from "@/lib/cn";

export function HeroChip({ dot, children }: { dot: string; children: ReactNode }) {
  return (
    <li className="inline-flex items-center gap-2 rounded-pill border border-navy-600 bg-navy-800 px-3 py-1.5 text-sm font-medium">
      <span className={cn("size-2 rounded-full", dot)} aria-hidden />
      {children}
    </li>
  );
}

export function Steps({ children }: { children: ReactNode[] }) {
  return (
    <ol className="grid gap-2.5">
      {children.map((step, i) => (
        <li
          key={i}
          className="grid grid-cols-[32px_minmax(0,1fr)] gap-3 rounded-card border border-line bg-surface px-4 py-3.5"
        >
          <span className="grid h-7 place-items-center rounded-lg bg-brand-50 font-numeric text-sm font-bold text-brand-500">
            {i + 1}
          </span>
          <p>{step}</p>
        </li>
      ))}
    </ol>
  );
}

export const NOTE_TONES = {
  gold: { box: "bg-gold-50", title: "text-gold-700" },
  amber: { box: "bg-warn-50", title: "text-warn-700" },
  red: { box: "bg-danger-50", title: "text-danger-700" },
  green: { box: "bg-money-50", title: "text-money-700" },
  brand: { box: "bg-brand-50", title: "text-brand-500" },
} as const;

export function Note({
  tone,
  title,
  children,
}: {
  tone: keyof typeof NOTE_TONES;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("grid gap-1 rounded-card px-4 py-3.5", NOTE_TONES[tone].box)}>
      <strong className={cn("font-semibold", NOTE_TONES[tone].title)}>{title}</strong>
      <div>{children}</div>
    </div>
  );
}

export function Cards({ children }: { children: ReactNode }) {
  return <div className="grid gap-3 sm:grid-cols-2">{children}</div>;
}

export function InfoCard({
  tag,
  title,
  children,
}: {
  tag: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="grid content-start gap-1.5 rounded-card border border-line bg-surface p-4">
      <span className="font-numeric text-xs text-ink-500">{tag}</span>
      <h3 className="font-bold text-ink-900">{title}</h3>
      <p>{children}</p>
    </div>
  );
}

export function GuideTable({ head, rows }: { head?: string[]; rows: ReactNode[][] }) {
  return (
    <div className="rounded-card border border-line bg-surface px-1">
      <Table className="-mx-1 px-1">
        {head && (
          <THead>
            {head.map((h) => (
              <TH key={h} className="uppercase tracking-wide">
                {h}
              </TH>
            ))}
          </THead>
        )}
        <TBody>
          {rows.map((row, r) => (
            <TR key={r}>
              {row.map((cell, c) => (
                <TD
                  key={c}
                  className={cn(
                    "align-top text-[0.94rem]",
                    c === 0 && "whitespace-nowrap font-semibold text-ink-900",
                  )}
                >
                  {cell}
                </TD>
              ))}
            </TR>
          ))}
        </TBody>
      </Table>
    </div>
  );
}

export function SyncChip({ tone, children }: { tone: PillTone; children: ReactNode }) {
  return (
    <Pill tone={tone} dot className="px-3.5 py-1.5 text-sm font-semibold">
      {children}
    </Pill>
  );
}

export function Path({ children }: { children: ReactNode }) {
  return <span className="whitespace-nowrap font-semibold text-ink-900">{children}</span>;
}

export function Code({ children }: { children: ReactNode }) {
  return (
    <code className="whitespace-nowrap rounded-md bg-brand-50 px-1.5 py-0.5 font-numeric text-[0.88em] text-ink-900">
      {children}
    </code>
  );
}
